/**
 * MembersPage — Tenant-scoped member management.
 *
 * Replaces the upstream `UserManagementPage` (which is global / instance-
 * admin) for the hosted multi-tenant deployment. Lists members of the
 * caller's *active organization*, plus pending invitations, and lets
 * org `owner`s and `admin`s invite/remove/update-role.
 *
 * Access control matches Better Auth's organization plugin defaults:
 *   - owner   → full access (can promote others to admin)
 *   - admin   → full access EXCEPT cannot demote/remove the owner
 *   - member  → read-only "contact an admin" notice
 *
 * Server-side enforcement lives in BA's organization plugin endpoints —
 * the UI checks here are purely for UX (hide buttons, show friendly
 * errors). A non-admin who calls /organization/invite-member directly
 * gets a 403 from BA.
 */

import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Users, X, type LucideIcon } from 'lucide-react';
import { useApiClient, PageLayout } from '@flowlib/ui';
import { useAuth } from '@flowlib/user-auth/ui';

// Hosted's `lucide-react` resolves through a different React major (18 vs.
// flow-backend's 19), so the icon's `LucideIcon` type is nominally distinct
// from the one `@flowlib/ui`'s `PageLayout` expects. The runtime shape is
// identical — the icon renders the same SVG either way — so we cast through
// `unknown` to satisfy tsc.
const UsersIcon = Users as unknown as LucideIcon;
const XIcon = X as unknown as LucideIcon;
import {
  type BAOrgInvitation,
  type BAOrgMember,
  buildAuthApiBase,
  cancelOrgInvitation,
  getActiveMemberRole,
  inviteOrgMember,
  listOrgInvitations,
  listOrgMembers,
  removeOrgMember,
  updateOrgMemberRole,
} from './auth-api';

const MANAGER_ROLES = new Set(['owner', 'admin']);

type LoadState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      role: string | null;
      members: BAOrgMember[];
      invitations: BAOrgInvitation[];
    }
  | { kind: 'error'; message: string };

export function MembersPage() {
  const api = useApiClient();
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const authApiBase = buildAuthApiBase(api.getBaseURL());

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [refreshTick, setRefreshTick] = useState(0);

  // Fetch role + members + invitations together. We re-fetch all three on
  // every mutation; the data set is small (org members) so the round-trips
  // aren't worth optimizing yet.
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    let cancelled = false;
    setState({ kind: 'loading' });

    const load = async () => {
      try {
        const role = await getActiveMemberRole(authApiBase);
        // Members + invitations for non-managers would 403; only ask if we
        // know we're allowed.
        const isManager = role !== null && MANAGER_ROLES.has(role);
        const [members, invitations] = isManager
          ? await Promise.all([listOrgMembers(authApiBase), listOrgInvitations(authApiBase)])
          : [[], []];
        if (cancelled) {
          return;
        }
        setState({ kind: 'ready', role, members, invitations });
      } catch (err) {
        if (cancelled) {
          return;
        }
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Failed to load members',
        });
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [authApiBase, isAuthenticated, refreshTick]);

  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  if (isAuthLoading) {
    return (
      <PageLayout title="Members" subtitle="Manage workspace members." icon={UsersIcon as never}>
        <p className="text-sm text-fl-muted-foreground">Loading…</p>
      </PageLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <PageLayout title="Members" subtitle="Manage workspace members." icon={UsersIcon as never}>
        <p className="text-sm text-fl-muted-foreground">Please sign in to access this page.</p>
      </PageLayout>
    );
  }

  if (state.kind === 'loading') {
    return (
      <PageLayout title="Members" subtitle="Manage workspace members." icon={UsersIcon as never}>
        <p className="text-sm text-fl-muted-foreground">Loading…</p>
      </PageLayout>
    );
  }

  if (state.kind === 'error') {
    return (
      <PageLayout title="Members" subtitle="Manage workspace members." icon={UsersIcon as never}>
        <div className="rounded-md border border-fl-destructive/30 bg-fl-destructive/10 p-3 text-sm text-fl-destructive">
          {state.message}
        </div>
      </PageLayout>
    );
  }

  const callerRole = state.role;
  const isManager = callerRole !== null && MANAGER_ROLES.has(callerRole);

  if (!isManager || callerRole === null) {
    return (
      <PageLayout title="Members" subtitle="Manage workspace members." icon={UsersIcon as never}>
        <div className="rounded-md border border-fl-border bg-fl-muted/30 p-3 text-sm text-fl-muted-foreground">
          Only workspace owners and admins can manage members. Contact a workspace admin for access.
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Members"
      subtitle="Invite, remove, and manage members of this workspace."
      icon={UsersIcon as never}
    >
      <div className="flex flex-col gap-8">
        <InviteSection authApiBase={authApiBase} onInvited={refresh} callerRole={callerRole} />
        <MembersSection
          authApiBase={authApiBase}
          members={state.members}
          callerUserId={user?.id ?? null}
          callerRole={callerRole}
          onChanged={refresh}
        />
        {state.invitations.length > 0 && (
          <InvitationsSection
            authApiBase={authApiBase}
            invitations={state.invitations}
            onChanged={refresh}
          />
        )}
      </div>
    </PageLayout>
  );
}

// ─────────────────────────────────────────────────────────────
// Invite a new member
// ─────────────────────────────────────────────────────────────

function InviteSection({
  authApiBase,
  callerRole,
  onInvited,
}: {
  authApiBase: string;
  callerRole: string;
  onInvited: () => void;
}) {
  const [email, setEmail] = useState('');
  // Only owners can grant the `admin` role, matching BA's default org access
  // rules. Admins can still invite as `member`.
  const canPromote = callerRole === 'owner';
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    setBusy(true);
    try {
      await inviteOrgMember(authApiBase, { email: email.trim(), role });
      setNotice(`Invitation sent to ${email.trim()}.`);
      setEmail('');
      setRole('member');
      onInvited();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send invitation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-fl-muted-foreground">
        Invite a member
      </h2>
      <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
        <div className="grid min-w-[260px] flex-1 gap-1">
          <label htmlFor="invite-email" className="text-xs font-medium">
            Email
          </label>
          <input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@example.com"
            autoComplete="email"
            required
            className="flex h-9 w-full rounded-md border border-fl-border bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-fl-ring"
          />
        </div>
        <div className="grid gap-1">
          <label htmlFor="invite-role" className="text-xs font-medium">
            Role
          </label>
          <select
            id="invite-role"
            value={role}
            onChange={(e) => setRole(e.target.value as 'member' | 'admin')}
            disabled={!canPromote}
            className="flex h-9 rounded-md border border-fl-border bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-fl-ring disabled:opacity-50"
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <button
          type="submit"
          disabled={busy}
          className="inline-flex h-9 items-center justify-center rounded-md bg-fl-primary px-4 py-2 text-sm font-medium text-fl-primary-foreground shadow transition-colors hover:bg-fl-primary/90 disabled:pointer-events-none disabled:opacity-50"
        >
          {busy ? 'Sending…' : 'Send invitation'}
        </button>
      </form>
      {!canPromote && (
        <p className="text-xs text-fl-muted-foreground">
          Only the workspace owner can invite new admins.
        </p>
      )}
      {error && <p className="text-xs text-fl-destructive">{error}</p>}
      {notice && <p className="text-xs text-emerald-500">{notice}</p>}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Members table
// ─────────────────────────────────────────────────────────────

function MembersSection({
  authApiBase,
  members,
  callerUserId,
  callerRole,
  onChanged,
}: {
  authApiBase: string;
  members: BAOrgMember[];
  callerUserId: string | null;
  callerRole: string;
  onChanged: () => void;
}) {
  const sorted = useMemo(
    () =>
      [...members].sort((a, b) => {
        // Owner first, then admin, then member; ties broken by name/email.
        const orderOf = (r: string) => (r === 'owner' ? 0 : r === 'admin' ? 1 : 2);
        const diff = orderOf(a.role) - orderOf(b.role);
        if (diff !== 0) {
          return diff;
        }
        const an = a.user?.name || a.user?.email || '';
        const bn = b.user?.name || b.user?.email || '';
        return an.localeCompare(bn);
      }),
    [members],
  );

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-fl-muted-foreground">
        Members ({members.length})
      </h2>
      <div className="overflow-hidden rounded-md border border-fl-border">
        <table className="w-full text-sm">
          <thead className="bg-fl-muted/30 text-left text-xs font-medium uppercase tracking-wide text-fl-muted-foreground">
            <tr>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Joined</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((m) => (
              <MemberRow
                key={m.id}
                authApiBase={authApiBase}
                member={m}
                callerUserId={callerUserId}
                callerRole={callerRole}
                onChanged={onChanged}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MemberRow({
  authApiBase,
  member,
  callerUserId,
  callerRole,
  onChanged,
}: {
  authApiBase: string;
  member: BAOrgMember;
  callerUserId: string | null;
  callerRole: string;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSelf = callerUserId === member.userId;
  const isOwner = member.role === 'owner';
  // Owner-row is read-only (transferring ownership is a separate flow we
  // haven't built yet — BA exposes /organization/transfer if/when needed).
  const canEdit = !isOwner && !isSelf;
  // Only the workspace owner can promote/demote admins. Admins can change
  // member<->member (no-op) but not touch other admins.
  const canChangeRole = canEdit && callerRole === 'owner';
  // Admins can remove members but not other admins; owner can remove anyone
  // except themselves.
  const canRemove =
    canEdit && (callerRole === 'owner' || (callerRole === 'admin' && member.role === 'member'));

  const handleRoleChange = async (newRole: string) => {
    if (newRole === member.role) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await updateOrgMemberRole(authApiBase, {
        memberId: member.id,
        role: newRole as 'member' | 'admin' | 'owner',
      });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update role');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    const label = member.user?.email || member.user?.name || 'this member';
    if (!confirm(`Remove ${label} from the workspace?`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await removeOrgMember(authApiBase, member.id);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove member');
      setBusy(false);
    }
  };

  const displayName = member.user?.name || '—';
  const displayEmail = member.user?.email || '—';
  const joined = formatDate(member.createdAt);

  return (
    <tr className="border-t border-fl-border">
      <td className="px-3 py-2">
        {displayName}
        {isSelf && <span className="ml-2 text-xs text-fl-muted-foreground">(you)</span>}
      </td>
      <td className="px-3 py-2 text-fl-muted-foreground">{displayEmail}</td>
      <td className="px-3 py-2">
        {canChangeRole ? (
          <select
            value={member.role}
            disabled={busy}
            onChange={(e) => handleRoleChange(e.target.value)}
            className="h-7 rounded border border-fl-border bg-transparent px-2 text-xs"
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        ) : (
          <span className="capitalize">{member.role}</span>
        )}
      </td>
      <td className="px-3 py-2 text-fl-muted-foreground">{joined}</td>
      <td className="px-3 py-2 text-right">
        {canRemove ? (
          <button
            type="button"
            onClick={handleRemove}
            disabled={busy}
            className="inline-flex h-7 items-center rounded border border-fl-border px-2 text-xs hover:bg-fl-destructive/10 hover:text-fl-destructive disabled:opacity-50"
          >
            Remove
          </button>
        ) : (
          <span className="text-xs text-fl-muted-foreground">—</span>
        )}
        {error && <p className="mt-1 text-xs text-fl-destructive">{error}</p>}
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────
// Pending invitations table
// ─────────────────────────────────────────────────────────────

function InvitationsSection({
  authApiBase,
  invitations,
  onChanged,
}: {
  authApiBase: string;
  invitations: BAOrgInvitation[];
  onChanged: () => void;
}) {
  const pending = useMemo(() => invitations.filter((i) => i.status === 'pending'), [invitations]);

  if (pending.length === 0) {
    return null;
  }

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-fl-muted-foreground">
        Pending invitations ({pending.length})
      </h2>
      <div className="overflow-hidden rounded-md border border-fl-border">
        <table className="w-full text-sm">
          <thead className="bg-fl-muted/30 text-left text-xs font-medium uppercase tracking-wide text-fl-muted-foreground">
            <tr>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Expires</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pending.map((inv) => (
              <InvitationRow
                key={inv.id}
                authApiBase={authApiBase}
                invitation={inv}
                onChanged={onChanged}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function InvitationRow({
  authApiBase,
  invitation,
  onChanged,
}: {
  authApiBase: string;
  invitation: BAOrgInvitation;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCancel = async () => {
    if (!confirm(`Cancel invitation to ${invitation.email}?`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await cancelOrgInvitation(authApiBase, invitation.id);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not cancel');
      setBusy(false);
    }
  };

  return (
    <tr className="border-t border-fl-border">
      <td className="px-3 py-2">{invitation.email}</td>
      <td className="px-3 py-2 capitalize">{invitation.role}</td>
      <td className="px-3 py-2 text-fl-muted-foreground">{formatDate(invitation.expiresAt)}</td>
      <td className="px-3 py-2 text-right">
        <button
          type="button"
          onClick={handleCancel}
          disabled={busy}
          className="inline-flex h-7 items-center gap-1 rounded border border-fl-border px-2 text-xs hover:bg-fl-destructive/10 hover:text-fl-destructive disabled:opacity-50"
        >
          <XIcon className="h-3 w-3" />
          Cancel
        </button>
        {error && <p className="mt-1 text-xs text-fl-destructive">{error}</p>}
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

function formatDate(iso: string | undefined): string {
  if (!iso) {
    return '—';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '—';
  }
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
